(() => {
  const mountedDetails = new WeakSet();

  const parseJson = (element, fallback) => {
    try {
      return JSON.parse(element?.textContent || '');
    } catch (_) {
      return fallback;
    }
  };

  const clampQuantity = (input, maximum) => {
    const raw = Number.parseInt(input.value, 10);
    const value = Number.isFinite(raw) ? raw : 0;
    const max = Number.isFinite(maximum) ? Math.max(0, maximum) : Number.MAX_SAFE_INTEGER;
    input.value = String(Math.min(max, Math.max(0, value)));
    return Number(input.value);
  };

  const setImage = (image, url, alt) => {
    if (!image || !url) return;
    if (image.src === url || image.currentSrc === url) return;
    image.classList.add('is-changing');
    const preloader = new Image();
    preloader.onload = () => {
      image.src = url;
      image.removeAttribute('srcset');
      if (typeof alt === 'string') image.alt = alt;
      requestAnimationFrame(() => image.classList.remove('is-changing'));
    };
    preloader.onerror = () => image.classList.remove('is-changing');
    preloader.src = url;
  };

  const setupDetail = (root) => {
    if (mountedDetails.has(root)) return;
    mountedDetails.add(root);

    const dataElement = root.querySelector('[data-beambox-variant-data]');
    const variants = parseJson(dataElement, []);
    if (!Array.isArray(variants) || variants.length === 0) return;

    const form = root.querySelector('[data-product-form]');
    const variantInput = root.querySelector('[data-variant-id-input]');
    const quantityInput = root.querySelector('[data-quantity-input]');
    const minusButton = root.querySelector('[data-quantity-minus]');
    const plusButton = root.querySelector('[data-quantity-plus]');
    const addButton = root.querySelector('[data-add-to-cart]');
    const buyButton = root.querySelector('[data-buy-now]');
    const paymentButton = root.querySelector('[data-payment-button]');
    const addLabel = root.querySelector('[data-add-label]');
    const message = root.querySelector('[data-product-message]');
    const price = root.querySelector('[data-product-price]');
    const comparePrice = root.querySelector('[data-product-compare-price]');
    const colourName = root.querySelector('[data-colour-name]');
    const swatches = [...root.querySelectorAll('[data-variant-id]')];
    const galleryItems = [...root.querySelectorAll('[data-gallery-item]')];
    const shoppingSection = root.querySelector('.product-shopping');
    const gallery = root.querySelector('.product-gallery');
    const purchasePanel = root.querySelector('.product-buy-panel');
    const introductionImage = root.querySelector('[data-introduction-product-image]');
    const productTitle = root.querySelector('.product-buy-panel__title')?.textContent?.trim() || '';
    const originalAddLabel = addLabel?.textContent?.trim() || 'Add';
    const usesStandardCheckout = root.dataset.checkoutMode === 'standard';
    const initialVariantId = variantInput?.value || '';
    const initialComparePrice = comparePrice?.textContent?.trim() || '';
    let currentVariant = variants.find((variant) => String(variant.id) === String(variantInput?.value)) || variants[0];

    const setupPurchasePanelSticky = () => {
      if (!shoppingSection || !gallery || !purchasePanel) return;

      const desktop = window.matchMedia('(min-width: 821px)');
      const headerComponent = document.querySelector('#header-component');
      let panelWidth = 0;
      let frame = 0;

      const stickyTop = () => {
        if (!headerComponent) return 28;
        const headerRect = headerComponent.getBoundingClientRect();
        const headerStyle = window.getComputedStyle(headerComponent);
        const headerIsVisible = headerRect.bottom > 0 && Number.parseFloat(headerStyle.opacity || '1') > 0.05;
        return headerIsVisible ? Math.max(28, Math.ceil(headerRect.bottom) + 16) : 28;
      };

      const clearPosition = () => {
        purchasePanel.classList.remove('is-fixed', 'is-at-gallery-end');
        purchasePanel.style.removeProperty('--beambox-sticky-left');
        purchasePanel.style.removeProperty('--beambox-sticky-top');
        purchasePanel.style.removeProperty('--beambox-sticky-width');
        purchasePanel.style.removeProperty('--beambox-sticky-end-top');
      };

      const measure = () => {
        clearPosition();
        panelWidth = purchasePanel.getBoundingClientRect().width;
      };

      const update = () => {
        frame = 0;
        if (!desktop.matches) {
          clearPosition();
          return;
        }

        if (!panelWidth) panelWidth = purchasePanel.getBoundingClientRect().width;
        const shoppingRect = shoppingSection.getBoundingClientRect();
        const galleryRect = gallery.getBoundingClientRect();
        const panelHeight = purchasePanel.offsetHeight;
        const fixedLeft = shoppingRect.right - panelWidth;
        const currentStickyTop = stickyTop();

        if (galleryRect.top >= currentStickyTop) {
          clearPosition();
          return;
        }

        if (galleryRect.bottom <= currentStickyTop + panelHeight) {
          purchasePanel.classList.remove('is-fixed');
          purchasePanel.classList.add('is-at-gallery-end');
          purchasePanel.style.setProperty('--beambox-sticky-width', `${panelWidth}px`);
          purchasePanel.style.setProperty(
            '--beambox-sticky-end-top',
            `${gallery.offsetTop + gallery.offsetHeight - panelHeight}px`
          );
          return;
        }

        purchasePanel.classList.remove('is-at-gallery-end');
        purchasePanel.classList.add('is-fixed');
        purchasePanel.style.setProperty('--beambox-sticky-left', `${fixedLeft}px`);
        purchasePanel.style.setProperty('--beambox-sticky-top', `${currentStickyTop}px`);
        purchasePanel.style.setProperty('--beambox-sticky-width', `${panelWidth}px`);
      };

      const requestUpdate = () => {
        if (frame) return;
        frame = requestAnimationFrame(update);
      };

      window.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('resize', () => {
        measure();
        requestUpdate();
      });
      desktop.addEventListener?.('change', () => {
        measure();
        requestUpdate();
      });
      if (headerComponent) {
        new MutationObserver(requestUpdate).observe(headerComponent, {
          attributes: true,
          attributeFilter: ['class', 'data-sticky-state', 'data-animating'],
        });
      }

      measure();
      update();
    };

    const announce = (text, isError = false) => {
      if (!message) return;
      message.textContent = text || '';
      message.classList.toggle('is-error', isError);
    };

    const inventoryMaximum = (variant) => {
      if (!variant?.inventoryManaged) return null;
      const quantity = Number(variant.inventoryQuantity);
      return Number.isFinite(quantity) ? Math.max(0, quantity) : null;
    };

    const isPurchaseAllowed = () => {
      const maximum = inventoryMaximum(currentVariant);
      const quantity = Number.parseInt(quantityInput?.value, 10);
      return Boolean(currentVariant?.available)
        && maximum !== 0
        && Number.isFinite(quantity)
        && quantity > 0;
    };

    const renderVariant = (variant, selectedSwatch) => {
      if (!variant) return;
      currentVariant = variant;
      const available = Boolean(variant.available);
      const maximum = inventoryMaximum(variant);

      if (variantInput) {
        variantInput.value = String(variant.id);
        // Keep Shopify's accelerated checkout element in sync with the
        // selected variant without changing the visible custom UI.
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (price) price.textContent = variant.price || '';
      if (comparePrice) {
        // Keep the server-rendered value as a fallback for the initially
        // selected variant while older cached variant JSON is being replaced.
        const compareText = variant.compareAtPrice || (
          String(variant.id) === String(initialVariantId) ? initialComparePrice : ''
        );
        comparePrice.hidden = !compareText;
        comparePrice.textContent = compareText;
      }

      swatches.forEach((swatch) => {
        const selected = selectedSwatch ? swatch === selectedSwatch : String(swatch.dataset.variantId) === String(variant.id);
        swatch.classList.toggle('is-selected', selected);
        swatch.setAttribute('aria-checked', String(selected));
      });

      if (selectedSwatch && colourName) colourName.textContent = selectedSwatch.dataset.colour || '';

      const gallery = Array.isArray(variant.gallery) && variant.gallery.length ? variant.gallery : [variant.featuredImage].filter(Boolean);
      galleryItems.forEach((item, index) => {
        const url = gallery[index];
        item.hidden = !url;
        const image = item.querySelector('img');
        if (url && image) setImage(image, url, `${productTitle} ${index + 1}`);
      });

      if (variant.featuredImage) setImage(introductionImage, variant.featuredImage, productTitle);

      if (quantityInput) {
        if (maximum !== null) {
          quantityInput.max = String(Math.max(0, maximum));
        } else {
          quantityInput.removeAttribute('max');
        }
        clampQuantity(quantityInput, maximum === null ? undefined : maximum);
      }

      const stockAvailable = available && maximum !== 0;
      const canPurchase = isPurchaseAllowed();
      if (addButton) addButton.disabled = !canPurchase;
      if (buyButton) buyButton.disabled = !canPurchase;
      if (paymentButton) {
        paymentButton.classList.toggle('is-disabled', !canPurchase);
        paymentButton.toggleAttribute('inert', !canPurchase);
      }
      if (addLabel) addLabel.textContent = stockAvailable ? originalAddLabel : 'Sold out';
      if (minusButton && quantityInput) minusButton.disabled = Number(quantityInput.value) <= 0;
      if (plusButton && maximum !== null && quantityInput) plusButton.disabled = Number(quantityInput.value) >= maximum;
      announce('');

      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', url);

      root.dispatchEvent(new CustomEvent('variant:selected', {
        bubbles: true,
        detail: { resource: variant },
      }));
    };

    swatches.forEach((swatch) => {
      swatch.addEventListener('click', () => {
        const variant = variants.find((item) => String(item.id) === String(swatch.dataset.variantId));
        renderVariant(variant, swatch);
      });
    });

    const updateQuantityButtons = () => {
      if (!quantityInput) return;
      const maximum = inventoryMaximum(currentVariant);
      const quantity = clampQuantity(quantityInput, maximum === null ? undefined : maximum);
      if (minusButton) minusButton.disabled = quantity <= 0;
      if (plusButton) plusButton.disabled = maximum !== null && quantity >= maximum;
      const canPurchase = isPurchaseAllowed();
      if (addButton) addButton.disabled = !canPurchase;
      if (buyButton) buyButton.disabled = !canPurchase;
      if (paymentButton) {
        paymentButton.classList.toggle('is-disabled', !canPurchase);
        paymentButton.toggleAttribute('inert', !canPurchase);
      }
    };

    minusButton?.addEventListener('click', () => {
      if (!quantityInput) return;
      quantityInput.value = String(Math.max(0, Number(quantityInput.value || 0) - 1));
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      updateQuantityButtons();
    });

    plusButton?.addEventListener('click', () => {
      if (!quantityInput) return;
      quantityInput.value = String(Number(quantityInput.value || 0) + 1);
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      updateQuantityButtons();
    });

    quantityInput?.addEventListener('input', updateQuantityButtons);
    quantityInput?.addEventListener('change', updateQuantityButtons);

    const dispatchCartUpdate = (result, quantity) => {
      // The theme's cart drawer listens to the same cart:update event as the
      // header cart bubble. Suppress its auto-open behavior for this custom
      // product form while still allowing the cart bubble and cart sections to
      // update normally.
      const autoOpenDrawers = [...document.querySelectorAll('cart-drawer-component[auto-open]')];
      autoOpenDrawers.forEach((drawer) => drawer.removeAttribute('auto-open'));

      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: result,
          sourceId: form?.id || root.id,
          data: {
            source: 'product-form-component',
            itemCount: quantity,
            productId: root.dataset.productId,
            variantId: String(currentVariant.id),
            sections: result.sections,
          },
        },
      }));

      // Restore the merchant's setting for other product forms and future
      // cart additions after all synchronous event listeners have run.
      if (autoOpenDrawers.length) {
        requestAnimationFrame(() => autoOpenDrawers.forEach((drawer) => drawer.setAttribute('auto-open', '')));
      }
    };

    const animateFlyToCart = () => {
      const cartIcon = document.querySelector('.header-actions__cart-icon');
      const imageUrl = currentVariant?.featuredImage;
      if (!cartIcon || !addButton || !imageUrl || !customElements.get('fly-to-cart')) return;

      const flyToCartElement = document.createElement('fly-to-cart');
      flyToCartElement.style.setProperty('background-image', `url(${imageUrl})`);
      flyToCartElement.source = addButton;
      flyToCartElement.destination = cartIcon;
      document.body.appendChild(flyToCartElement);
    };

    const addCurrentVariant = async ({ notifyCart = true } = {}) => {
      if (!currentVariant?.available || inventoryMaximum(currentVariant) === 0 || !quantityInput) {
        throw new Error('This option is sold out.');
      }
      const quantity = clampQuantity(quantityInput, inventoryMaximum(currentVariant) ?? undefined);
      if (quantity <= 0) throw new Error('Select a quantity greater than 0.');
      const cartItems = [...document.querySelectorAll('cart-items-component[data-section-id]')];
      const sectionIds = cartItems.map((item) => item.dataset.sectionId).filter(Boolean);
      const payload = {
        items: [{ id: Number(currentVariant.id), quantity }],
      };
      if (sectionIds.length) payload.sections = sectionIds.join(',');
      payload.sections_url = window.location.pathname;

      const response = await fetch(root.dataset.cartAddUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add this item.');

      if (notifyCart) {
        dispatchCartUpdate(result, quantity);
        animateFlyToCart();
      }
      return result;
    };

    buyButton?.addEventListener('click', async () => {
      if (!usesStandardCheckout || buyButton.disabled || !isPurchaseAllowed()) return;

      buyButton.disabled = true;
      buyButton.setAttribute('aria-busy', 'true');
      announce('Preparing checkout…');
      try {
        await addCurrentVariant({ notifyCart: false });
        const shopRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        window.location.assign(`${shopRoot}checkout`);
      } catch (error) {
        announce(error instanceof Error ? error.message : 'Unable to start checkout.', true);
        buyButton.disabled = !isPurchaseAllowed();
      } finally {
        buyButton.removeAttribute('aria-busy');
      }
    });

    form?.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!addButton || addButton.disabled || !isPurchaseAllowed()) return;
      addButton.disabled = true;
      addButton.setAttribute('aria-busy', 'true');
      announce('Adding…');
      try {
        await addCurrentVariant({ notifyCart: true });
        // The header cart bubble is the visual confirmation. Keep this local
        // status area empty so Add behaves like the compact mobile pattern.
        announce('');
      } catch (error) {
        announce(error instanceof Error ? error.message : 'Unable to add this item.', true);
      } finally {
        addButton.removeAttribute('aria-busy');
        addButton.disabled = !isPurchaseAllowed();
      }
    });

    setupPurchasePanelSticky();
    renderVariant(currentVariant, swatches.find((swatch) => String(swatch.dataset.variantId) === String(currentVariant.id)));
  };

  const mount = (scope = document) => {
    scope.querySelectorAll?.('[data-beambox-product-detail]').forEach(setupDetail);
  };

  mount();
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
})();
